import { BrandLoader } from '@/components/BrandLoader';
import { LogoMark } from '@/components/LogoMark';
import { ApiClientError } from '@/lib/apiClient';
import { CheckCircle2, LogIn, LogOut, ShieldAlert } from 'lucide-react';
import { CF_ACCESS_FULL_LOGOUT_URL, clearLoggedOutFlag, isJustLoggedOut, useAuth } from './useAuth';

/**
 * AuthGuard — wrap routy ktore wymagaja zalogowanego usera.
 * - Just-logged-out flag (sessionStorage) → LoggedOutScreen (brand-owned, soft logout)
 * - Loading → splash
 * - 403 (USER_NOT_ACTIVE) → ekran "konto czeka na aktywacje"
 * - 401 → LoginRequiredScreen (cold start, expired session)
 * - 200 → renderuj children
 *
 * Flag check przed isLoading — soft logout nie powinien renderowac dashboardu
 * nawet na chwile (cache wyczyszczony, ale CF Access cookie wciaz valid wiec
 * /auth/me by zwrocil 200 i pokazal dashboard).
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading, error } = useAuth();

  // Soft logout — sessionStorage flag wskazuje ze user wlasnie kliknal "Wyloguj"
  // (cookie CF moze byc wciaz valid, ale pokazujemy LoggedOutScreen az do "Zaloguj ponownie")
  if (isJustLoggedOut()) {
    return <LoggedOutScreen />;
  }

  if (isLoading) {
    return <SplashLoader />;
  }

  // Chwilowy błąd odświeżenia sesji (brak sieci, blip D1) przy znanym userze NIE
  // wyrzuca aplikacji — inaczej przepada niezapisany formularz. Ekrany błędów tylko
  // gdy nie wiemy, kto to, albo serwer mówi wprost: sesja wygasła / konto nieaktywne.
  const authProblem =
    error instanceof ApiClientError && (error.status === 401 || error.code === 'USER_NOT_ACTIVE');
  if (error && (!user || authProblem)) {
    if (error instanceof ApiClientError) {
      if (error.code === 'USER_NOT_ACTIVE') {
        return <PendingActivationScreen email={(error.details as { email?: string })?.email} />;
      }
      if (error.status === 401) {
        return <LoginRequiredScreen />;
      }
    }
    return <ErrorScreen error={error} />;
  }

  if (!user) {
    return <SplashLoader />;
  }

  return <>{children}</>;
}

// ===== Sub-components =====

function SplashLoader() {
  // Ten sam ekran co powitanie/wylogowanie (BrandLoader) — bez skoku wygladu.
  return <BrandLoader />;
}

function PendingActivationScreen({ email }: { email?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
      <div className="card w-full max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning-subtle">
          <ShieldAlert className="h-6 w-6 text-warning" strokeWidth={2} />
        </div>
        <h1 className="text-xl font-semibold text-fg">Konto czeka na aktywację</h1>
        <p className="mt-3 text-sm text-fg-muted">
          Twoje konto <span className="text-fg">{email ?? '(brak adresu)'}</span> jest już założone
          — administrator musi je jeszcze włączyć, zanim zaczniesz korzystać z CRM.
        </p>
        <p className="mt-4 text-xs text-fg-subtle">Skontaktuj się z Dawidem (dawidfc@gmail.com).</p>
      </div>
    </div>
  );
}

function LoginRequiredScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
      <div className="card w-full max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-subtle">
          <LogIn className="h-6 w-6 text-accent" strokeWidth={2} />
        </div>
        <div className="my-2 flex justify-center">
          <LogoMark size="sm" />
        </div>
        <h1 className="mt-4 text-xl font-semibold text-fg">Wymagane logowanie</h1>
        <p className="mt-3 text-sm text-fg-muted">
          Zaloguj się — jednorazowy kod przyjdzie na Twój firmowy e-mail.
        </p>
        <button
          type="button"
          onClick={() => {
            // CF Access redirect — leci na app domain bez ścieżki, a serwer
            // odpowiada 302 → coreltb.cloudflareaccess.com/cdn-cgi/access/login/...
            // z konkretnymi query params (kid, meta). Bezpośredni link na
            // /cdn-cgi/access/login (relatywny) zwraca 404 — to nie istnieje na
            // app domain, tylko na team domain z query string'ami które CF
            // generuje sam.
            // Pełen reload zrzuca cache TanStack Query, browser robi GET /,
            // CF Access wykrywa brak ważnej sesji i robi własny redirect.
            window.location.href = '/';
          }}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover"
        >
          <LogIn className="h-4 w-4" strokeWidth={2} />
          Zaloguj się
        </button>
      </div>
    </div>
  );
}

/**
 * LoggedOutScreen — brand-owned ekran po wylogowaniu.
 * Soft logout: cookie CF Access wciaz valid, czyscimy tylko cache + flag.
 *
 * Primary CTA: "Zaloguj ponownie" — clear flag + reload do dashboardu (cookie valid → bez OTP)
 * Secondary: "Wyloguj calkowicie z Cloudflare" — team-domain logout dla pelnego unievalidowania
 * sesji (np. zmiana usera na sluzbowym kompie).
 */
function LoggedOutScreen() {
  const handleLoginAgain = () => {
    clearLoggedOutFlag();
    // Reload zeby AuthGuard wykonal /auth/me i pokazal dashboard (cookie wciaz valid)
    window.location.assign('/');
  };

  const handleFullLogout = () => {
    clearLoggedOutFlag();
    window.location.assign(CF_ACCESS_FULL_LOGOUT_URL);
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
      <div className="card w-full max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle">
          <CheckCircle2 className="h-6 w-6 text-success" strokeWidth={2} />
        </div>
        <div className="my-2 flex justify-center">
          <LogoMark size="sm" />
        </div>
        <h1 className="mt-4 text-xl font-semibold text-fg">Wylogowano</h1>
        <p className="mt-3 text-sm text-fg-muted">Żeby wrócić do pracy, zaloguj się jeszcze raz.</p>
        <button
          type="button"
          onClick={handleLoginAgain}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover"
        >
          <LogIn className="h-4 w-4" strokeWidth={2} />
          Zaloguj się ponownie
        </button>

        {/* Secondary action — pelny CF Access logout (zmiana usera, sesja zostawiona u znajomego) */}
        <div className="mt-6 border-t border-border pt-4">
          <p className="text-xs text-fg-subtle">Inny użytkownik korzysta z tego komputera?</p>
          <button
            type="button"
            onClick={handleFullLogout}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted hover:text-fg"
          >
            <LogOut className="h-3 w-3" strokeWidth={2} />
            Wyloguj całkowicie (też z logowania kodem)
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ error }: { error: Error }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
      <div className="card w-full max-w-md p-8 text-center">
        <h1 className="text-xl font-semibold text-error">Błąd ładowania użytkownika</h1>
        <p className="mt-3 text-sm text-fg-muted">{error.message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
        >
          Spróbuj ponownie
        </button>
      </div>
    </div>
  );
}
